import { useEffect, useRef, useState } from 'react';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { Drawer } from '@/ui/Drawer';
import { Dialog } from '@/ui/Dialog';
import { Field, Textarea } from '@/ui/Field';
import { Skeleton } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import { cn } from '@/lib/cn';
import { Filtros, type Chip } from './Filtros';
import { EstadoBadge, ValorDeFormulario, FlujoDePasos, Historial, Adjuntos } from './piezas';
import {
  diasDeRango, esCerrada, useCancelar, useDetalle, useMias, useAdjuntar,
  type FiltroMias, type SolicitudDetalle,
} from './api';

const TAM = 20;

const CHIPS: Chip[] = [
  // El de atención solo existe cuando hay algo que atender: un «0» fijo enseña
  // a ignorarlo, y entonces deja de servir el día que marque 1.
  { clave: 'atencion', texto: 'Requieren tu atención', icono: 'bi-exclamation-triangle-fill', soloSiHay: true },
  { clave: 'abiertas', texto: 'En curso', icono: 'bi-hourglass-split' },
  { clave: 'cerradas', texto: 'Cerradas', icono: 'bi-check2-all' },
  { clave: 'todas', texto: 'Todas', icono: 'bi-list-ul' },
];

/**
 * Mis solicitudes.
 *
 * Arranca en «En curso» y no en «Todas»: lo que el colaborador viene a mirar es
 * lo que sigue su camino. El histórico está a un clic y con su contador a la
 * vista, así que no se esconde nada.
 */
export function MisSolicitudes({ onCorregir }: { onCorregir: (r: SolicitudDetalle) => void }) {
  const [filtro, setFiltro] = useState<FiltroMias>({ estado: 'abiertas', q: '' });
  const [texto, setTexto] = useState('');
  const [limite, setLimite] = useState(TAM);
  const [abierta, setAbierta] = useState<number | null>(null);

  // Rebote de la búsqueda. 350 ms, el mismo que tenía.
  useEffect(() => {
    const t = window.setTimeout(() => {
      setFiltro((f) => (f.q === texto.trim() ? f : { ...f, q: texto.trim() }));
      setLimite(TAM);
    }, 350);
    return () => window.clearTimeout(t);
  }, [texto]);

  const consulta = useMias(filtro, limite);
  const datos = consulta.data;
  const resumen = datos?.resumen ?? {};
  const filas = datos?.items ?? [];

  return (
    <div className="flex flex-col gap-4">
      <Filtros
        chips={CHIPS}
        activo={filtro.estado}
        cuentas={resumen as Record<string, number>}
        onElegir={(k) => { setFiltro((f) => ({ ...f, estado: k })); setLimite(TAM); }}
        busqueda={texto}
        onBuscar={setTexto}
        etiquetaBusqueda="Buscar por radicado o tipo"
        marcador="Buscar radicado o tipo…"
      />

      {consulta.isError ? (
        <EstadoError err={consulta.error} que="tus solicitudes"
                     onReintentar={() => void consulta.refetch()} />
      ) : consulta.isLoading ? (
        <Card><div className="flex flex-col gap-2 p-4">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
        </div></Card>
      ) : filas.length === 0 ? (
        <EstadoVacio icono="bi-clipboard-check" titulo={vacio(filtro, resumen)} />
      ) : (
        <>
          <Card className="overflow-x-auto">
            {/* `min-w` y no columnas elásticas: con seis columnas en 343 px, el
                  reparto automático dejaba «Tipo» en 55 px y partía las palabras
                  por la mitad («Per / miso / Espec / ial»). La tabla mantiene su
                  ancho y se desliza dentro de su tarjeta; el documento no se
                  mueve. */}
              <table aria-label="Mis solicitudes"
                   className="w-full min-w-[46rem] border-collapse text-3">
              <thead>
                <tr className="border-b border-line text-left text-2 uppercase tracking-wide text-muted">
                  <th scope="col" className="px-4 py-2.5 font-semibold">Radicado</th>
                  <th scope="col" className="px-4 py-2.5 font-semibold">Tipo</th>
                  <th scope="col" className="px-4 py-2.5 font-semibold">Estado</th>
                  <th scope="col" className="px-4 py-2.5 font-semibold">Esperando a</th>
                  <th scope="col" className="px-4 py-2.5 font-semibold">Fecha</th>
                  <th className="px-4 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {filas.map((r) => (
                  <tr key={r.id}
                      className={cn('border-b border-line last:border-0',
                                    r.accion_requerida && 'bg-warn/8')}>
                    <td className="whitespace-nowrap px-4 py-3 font-mono text-2 text-ink-2">{r.radicado}</td>
                    <td className="px-4 py-3">
                      {/* `nowrap`: sin él, el reparto automático de la tabla
                          estrangulaba esta columna a 55 px y partía «Permiso
                          Especial QA» en cuatro trozos de tres letras. Prefiere
                          ensanchar la tabla —que se desliza dentro de su
                          tarjeta— a picar las palabras. */}
                      <span className="whitespace-nowrap font-medium text-ink">{r.type_name}</span>
                      {r.accion_requerida && (
                        <span className="mt-1 flex items-center gap-1.5 text-2 font-semibold text-warn-ink">
                          <i className={cn('bi', r.status === 'devuelta'
                            ? 'bi-arrow-counterclockwise' : 'bi-paperclip')} aria-hidden="true" />
                          {r.motivo_accion || 'Requiere tu atención'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3"><EstadoBadge estado={r.status} /></td>
                    <td className="whitespace-nowrap px-4 py-3 text-2 text-muted">{r.esperando || '—'}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-2 text-muted">{Utils.fmtDate(r.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="ghost" onClick={() => setAbierta(r.id)}>
                        <i className="bi bi-eye" aria-hidden="true" /> Ver
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          {filas.length < (datos?.total ?? 0) && (
            <div className="flex justify-center">
              <Button variant="secondary" size="sm" loading={consulta.isFetching}
                      onClick={() => setLimite((l) => l + TAM)}>
                Mostrar más ({filas.length} de {datos?.total})
              </Button>
            </div>
          )}
        </>
      )}

      <DetalleSolicitud
        id={abierta}
        onCerrar={() => setAbierta(null)}
        onCorregir={(r) => { setAbierta(null); onCorregir(r); }}
      />
    </div>
  );
}

/** El vacío tiene que decir POR QUÉ está vacío: no es lo mismo «no has enviado
 *  nada nunca» que «no tienes nada en curso, pero sí 14 cerradas». */
function vacio(f: FiltroMias, res: { cerradas?: number }): string {
  if (f.q) return `Ningún resultado para «${f.q}»`;
  if (f.estado === 'abiertas' && (res.cerradas ?? 0) > 0) {
    return `No tienes solicitudes en curso. Tienes ${res.cerradas} en el historial.`;
  }
  if (f.estado === 'cerradas') return 'Todavía no tienes solicitudes cerradas';
  if (f.estado === 'atencion') return 'No hay nada pendiente de tu parte';
  return 'Aún no has enviado solicitudes';
}

/* ── El detalle ────────────────────────────────────────────────────────── */

function DetalleSolicitud({
  id, onCerrar, onCorregir,
}: { id: number | null; onCerrar: () => void; onCorregir: (r: SolicitudDetalle) => void }) {
  const consulta = useDetalle(id);
  const [cancelando, setCancelando] = useState(false);
  const r = consulta.data;
  const cerrada = esCerrada(r?.status);

  return (
    <>
      <Drawer
        abierto={id != null}
        onCambio={(v) => { if (!v) onCerrar(); }}
        titulo={r ? `Solicitud ${r.radicado}` : 'Solicitud'}
        descripcion={r?.type_name ?? undefined}
        ancho="lg"
        pie={
          <>
            {r && !cerrada && (
              // «Cancelar solicitud» a la izquierda y en tono discreto: es una
              // salida, no la acción de la pantalla.
              <Button variant="ghost" className="mr-auto text-danger-ink"
                      onClick={() => setCancelando(true)}>
                <i className="bi bi-x-circle" aria-hidden="true" /> Cancelar solicitud
              </Button>
            )}
            <Button variant="secondary" onClick={onCerrar}>Cerrar</Button>
          </>
        }
      >
        {consulta.isLoading && (
          <div className="flex flex-col gap-3">
            {[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
          </div>
        )}
        {consulta.isError && (
          <EstadoError err={consulta.error} que="esta solicitud"
                       onReintentar={() => void consulta.refetch()} />
        )}
        {r && (
          <div className="flex flex-col gap-5">
            <div><EstadoBadge estado={r.status} /></div>
            <BloqueDeMiSolicitud r={r} onCorregir={() => onCorregir(r)} />

            <Seccion titulo="Datos">
              {(r.fields ?? []).filter((f) => f.type !== 'file').map((f) => (
                <div key={f.key} className="flex flex-wrap gap-x-2 py-1">
                  <span className="text-2 uppercase tracking-wide text-muted">{f.label}</span>
                  <span className="w-full text-3 text-ink">
                    <ValorDeFormulario valor={r.form_data?.[f.key]} />
                  </span>
                </div>
              ))}
              {!(r.fields ?? []).some((f) => f.type !== 'file') && (
                <p className="text-3 text-muted">—</p>
              )}
            </Seccion>

            <Adjuntos lista={r.attachments} />
            <Seccion titulo="Flujo de aprobación">
              <FlujoDePasos flujo={r.flow} pasoActual={r.current_step} estado={r.status} />
            </Seccion>
            <Historial lista={r.approvals} />
          </div>
        )}
      </Drawer>

      {r && (
        <ConfirmarCancelacion
          abierto={cancelando}
          solicitud={r}
          onCerrar={() => setCancelando(false)}
          onHecho={() => { setCancelando(false); onCerrar(); }}
        />
      )}
    </>
  );
}

function Seccion({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="mb-2 text-2 font-semibold uppercase tracking-wide text-muted">{titulo}</h3>
      <div className="rounded-sm border border-line bg-surface px-4 py-3">{children}</div>
    </section>
  );
}

/**
 * Lo que el colaborador necesita para NO quedarse esperando sin saber por qué:
 * qué falta de su parte, en manos de quién está, y si chocó con algo suyo.
 *
 * Los tres datos ya venían en la respuesta de `GET /{id}` desde que se
 * construyó la pantalla del aprobador; la vista del solicitante los descartaba.
 * El caso que cierra es el peor: un soporte obligatorio sin adjuntar bloquea la
 * aprobación —incapacidad, luto: requisito legal—, el aprobador ve el motivo y
 * el solicitante no, así que el trámite se quedaba quieto sin que su dueño
 * pudiera hacer nada.
 */
function BloqueDeMiSolicitud({ r, onCorregir }: { r: SolicitudDetalle; onCorregir: () => void }) {
  const cerrada = esCerrada(r.status);
  const faltan = (r.soportes_faltantes ?? []).filter((f) => f.key);
  const propios = r.solapes_propios ?? [];
  const appr = r.current_approver;
  const paso = (r.flow ?? [])[r.current_step];

  return (
    <div className="flex flex-col gap-3">
      {/* Lo primero de todo: es lo único que no avanza sin el colaborador. */}
      {r.status === 'devuelta' && (
        <Aviso tono="warn" icono="bi-arrow-counterclockwise"
               titulo="Te devolvieron esta solicitud para que la corrijas">
          {r.devolucion?.note && (
            <p className="mt-1.5 rounded-sm bg-surface px-3 py-2 text-3 leading-relaxed text-ink">
              {r.devolucion.note}
            </p>
          )}
          <p className="mt-1.5 text-2 leading-relaxed">
            {r.devolucion?.approver_name
              ? <>Devuelta por <strong>{r.devolucion.approver_name}</strong></> : 'Devuelta'}
            {r.devolucion?.step_label && <> en el paso <strong>{r.devolucion.step_label}</strong></>}
            {r.devolucion?.created_at && <> · {Utils.fmtDate(r.devolucion.created_at)}</>}.
            {' '}Corrígela y vuelve a enviarla: conserva el mismo radicado y su historial.
          </p>
          <Button size="sm" className="mt-3" onClick={onCorregir}>
            <i className="bi bi-pencil-square" aria-hidden="true" /> Corregir y reenviar
          </Button>
        </Aviso>
      )}

      {faltan.length > 0 && !cerrada && (
        <Aviso tono="danger" icono="bi-paperclip"
               titulo={faltan.length === 1
                 ? 'Falta un soporte obligatorio'
                 : `Faltan ${faltan.length} soportes obligatorios`}>
          <p className="mt-1 text-2">Tu solicitud no se puede aprobar hasta que lo adjuntes.</p>
          <div className="mt-2 flex flex-col gap-2">
            {faltan.map((f) => <AdjuntarAhora key={f.key} id={r.id} campo={f} />)}
          </div>
        </Aviso>
      )}

      {appr && !cerrada && (
        appr.resolved ? (
          <p className="flex items-center gap-2 rounded-sm border border-line bg-sunken px-3 py-2 text-2 text-ink-2">
            <i className="bi bi-person-check shrink-0" aria-hidden="true" />
            <span>En manos de <strong>{appr.approver_name || '—'}</strong>
              {paso && <> · paso <strong>{paso.label}</strong></>}</span>
          </p>
        ) : (
          // `approver_name` ya dice «Jefe inmediato (no asignado)», que repite la
          // etiqueta del paso — se usa el paso y se omite el nombre.
          <Aviso tono="warn" icono="bi-exclamation-triangle" titulo="Sin aprobador asignado">
            <p className="mt-1 text-2 leading-relaxed">
              Nadie tiene asignado el paso <strong>{paso?.label || '—'}</strong>, así que tu
              solicitud no puede avanzar. Escríbele a Bienestar para que asignen a quien debe
              aprobarla.
            </p>
          </Aviso>
        )
      )}

      {propios.length > 0 && (
        <Aviso tono="warn" icono="bi-calendar-x"
               titulo={propios.length === 1
                 ? 'Ya tienes otra solicitud en esas fechas'
                 : `Ya tienes otras ${propios.length} solicitudes en esas fechas`}>
          <ul className="mt-1.5 list-disc pl-5 text-2 leading-relaxed">
            {propios.map((p, i) => (
              <li key={i}>{p.tipo} · {p.inicio} a {p.fin} <em>({p.estado})</em></li>
            ))}
          </ul>
        </Aviso>
      )}
    </div>
  );
}

export function Aviso({
  tono, icono, titulo, children,
}: {
  tono: 'warn' | 'danger' | 'info';
  icono: string; titulo: string; children?: React.ReactNode;
}) {
  const clases = tono === 'danger' ? 'border-danger/40 bg-danger/10 text-danger-ink'
    : tono === 'info' ? 'border-info/40 bg-info/10 text-info-ink'
    : 'border-warn/40 bg-warn/12 text-warn-ink';
  return (
    <div className={cn('rounded-sm border px-4 py-3', clases)}>
      <p className="flex items-center gap-2 text-3 font-semibold">
        <i className={cn('bi', icono, 'shrink-0')} aria-hidden="true" />{titulo}
      </p>
      {children}
    </div>
  );
}

/** Sube un soporte que faltaba. Se recarga el detalle entero en vez de tachar la
 *  línea: el servidor decide si con esto ya quedó completa, y de paso avisa al
 *  aprobador. */
function AdjuntarAhora({ id, campo }: { id: number; campo: { key: string; label: string } }) {
  const adjuntar = useAdjuntar();
  const entrada = useRef<HTMLInputElement>(null);

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-sm bg-surface px-3 py-2">
      <span className="text-3 font-medium text-ink">{campo.label}</span>
      <input
        ref={entrada}
        type="file"
        className="sr-only"
        aria-label={`Adjuntar ${campo.label}`}
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (!file) return;
          if (file.size > 15 * 1024 * 1024) { Utils.toast('El archivo supera 15MB', 'error'); return; }
          try {
            await adjuntar.mutateAsync({ id, fieldKey: campo.key, file });
            Utils.toast('Soporte adjuntado', 'success');
          } catch (err) {
            Utils.toast(err instanceof Error ? err.message : 'No se pudo adjuntar', 'error');
          }
        }}
      />
      <Button size="sm" loading={adjuntar.isPending} onClick={() => entrada.current?.click()}>
        <i className="bi bi-upload" aria-hidden="true" /> Adjuntar ahora
      </Button>
    </div>
  );
}

/**
 * Retirar la propia solicitud.
 *
 * No usa el `ConfirmDialog` genérico porque ahí los botones se llaman «Cancelar»
 * y «Confirmar»: en esta pantalla «Cancelar» significaría a la vez *no hacer
 * nada* y *cancelar el trámite*. Con «Volver» y «Sí, cancelar la solicitud» no
 * hay forma de equivocarse.
 *
 * El motivo es opcional pero se pide: es lo único que le llega al jefe que la
 * estaba esperando, y queda en la auditoría.
 */
function ConfirmarCancelacion({
  abierto, solicitud: r, onCerrar, onHecho,
}: { abierto: boolean; solicitud: SolicitudDetalle; onCerrar: () => void; onHecho: () => void }) {
  const cancelar = useCancelar();
  const [nota, setNota] = useState('');

  const dias = (r.fields ?? [])
    .filter((f) => f.type === 'rango_fechas')
    .map((f) => {
      const v = r.form_data?.[f.key];
      return v && typeof v === 'object' && !Array.isArray(v)
        ? diasDeRango(v.inicio, v.fin) : null;
    })
    .filter((n): n is number => Boolean(n))
    .reduce((a, b) => a + b, 0);

  return (
    <Dialog
      abierto={abierto}
      onCambio={(v) => { if (!v) onCerrar(); }}
      titulo="Cancelar la solicitud"
      ancho="max-w-lg"
      pie={
        <>
          <Button variant="ghost" onClick={onCerrar}>Volver</Button>
          <Button
            variant="destructive"
            loading={cancelar.isPending}
            onClick={async () => {
              try {
                await cancelar.mutateAsync({ id: r.id, note: nota });
                Utils.toast('Solicitud cancelada', 'success');
                onHecho();
              } catch (err) {
                Utils.toast(err instanceof Error ? err.message : 'No se pudo cancelar', 'error');
              }
            }}
          >
            Sí, cancelar la solicitud
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-3 leading-relaxed text-ink">
          Vas a retirar tu solicitud <strong>{r.radicado}</strong>
          {r.type_name && <> ({r.type_name})</>}.
        </p>
        <ul className="list-disc pl-5 text-3 leading-relaxed text-ink-2">
          <li>Saldrá del flujo de aprobación y nadie tendrá que decidirla.</li>
          {dias > 0 && (
            <li>Los <strong>{dias} día{dias === 1 ? '' : 's'}</strong> que reservaba quedarán
              libres para volver a pedirlos.</li>
          )}
          <li>Queda registrada como <em>cancelada</em>, que no es lo mismo que rechazada.</li>
          <li><strong>No se puede deshacer.</strong> Si la necesitas de nuevo, tendrás que
            radicarla otra vez.</li>
        </ul>
        <Field label="Motivo" opcional ayuda="Se le enviará a quien la estaba esperando."
               id="sol-cancel-note">
          <Textarea rows={2} value={nota} onChange={(e) => setNota(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}
